import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import invariant from 'invariant';
import { createContext, useContextSelector } from 'use-context-selector';
import { useApolloClient } from '@frameio/graphql-client';
import type { AssetType } from '../types';

type AssetContextValue = {
  /** The asset type */
  assetType: AssetType;
  /** The minimum data needed to lookup the Asset */
  id: string | undefined;
};

const AssetContext = createContext<AssetContextValue>({
  assetType: 'file',
  id: undefined,
});

export type AssetContextProps = PropsWithChildren<AssetContextValue>;

/**
 * The AssetContext provides the minimum asset information needed for all the
 * various useFragment calls used throughout the Asset components.
 */
export function AssetContextProvider({
  assetType,
  id,
  children,
}: AssetContextProps) {
  const value = useMemo<AssetContextValue>(
    () => ({
      assetType,
      id,
    }),
    [assetType, id]
  );

  return (
    <AssetContext.Provider value={value}>{children}</AssetContext.Provider>
  );
}

/**
 * Get the id of the current asset.
 *
 * @returns id The asset id
 */
export const useAssetId = () =>
  useContextSelector(AssetContext, ({ id }) => {
    if (typeof id === 'undefined') {
      invariant(id, 'Missing assetId in AssetContext.');
    }

    return id;
  });

/**
 * Gets the cache key for the current asset, helps with easier use of useFragment
 * hooks.
 *
 * @return key The Apollo cache key
 */
export function useAssetKey() {
  const { cache } = useApolloClient();
  const id = useAssetId();

  const key = useMemo(
    () =>
      cache.identify({
        __typename: 'Asset',
        id,
      }),
    [cache, id]
  );

  return key ?? '';
}

export const useAssetType = () =>
  useContextSelector(AssetContext, ({ assetType }) => assetType);
